import { AddInstantBillFormWidgetFields } from '@melio/ap-widgets';
import { useToast } from '@melio/penny';
import { Traits, useAnalytics, withAnalyticsContext } from '@melio/platform-analytics';
import {
  Bill,
  PaymentIntent,
  useAccountingPlatforms,
  useBills,
  useFiles,
  usePaymentIntents,
  useVendors,
} from '@melio/platform-api';
import { FeatureFlags, useFeature } from '@melio/platform-feature-flags';
import { useDateUtils } from '@melio/platform-utils';

import { useTrackInstantBillCreation } from '../instant-bill-utils';
import { AddInstantBillScreen } from './screens';

export type AddInstantBillActivityProps = {
  defaultValues?: Partial<AddInstantBillFormWidgetFields>;
  onDone: (id: PaymentIntent['id'] | Bill['id']) => void;
  onClose: VoidFunction;
  onGoToDashboard: VoidFunction;
  onError?: ErrorFunction;
};

export const AddInstantBillActivity = withAnalyticsContext<AddInstantBillActivityProps>(
  ({ onClose, onDone, onError, onGoToDashboard, defaultValues }) => {
    const { isFetching, activeAccountingPlatform } = useAccountingPlatforms();

    const { isMutating: isCreatingPaymentIntent } = usePaymentIntents({ enabled: false });
    const { create: createBill, isMutating: isCreatingBill } = useBills({ enabled: false });
    const { create: createFile, isMutating: isCreatingFile } = useFiles({ enabled: false });
    const toast = useToast();
    const { createDate } = useDateUtils();
    const today = createDate();
    const isSaving = isCreatingPaymentIntent || isCreatingFile || isCreatingBill;
    const { data: vendors, isLoading: isVendorsLoading, create: createVendor } = useVendors();

    const [isDisplayVendorAsFreeTextInstantBillEnabled] = useFeature(
      FeatureFlags.DisplayVendorAsFreeTextInstantBill,
      false
    );

    const shouldDisplayVendorInputAsFreeText =
      isDisplayVendorAsFreeTextInstantBillEnabled && !isVendorsLoading && !vendors?.length;

    const handleFail = (error: PlatformError) => {
      toast({ type: 'error', title: error.message || 'Failed to create the bill' });
      onError?.(error);
    };

    const { setTraits } = useAnalytics();
    const { trackInstantBillCreation } = useTrackInstantBillCreation();
    const setBillTraits = () => {
      const traits: Traits = {
        create_a_bill: true,
        last_bill_added_date: createDate().toISOString(),
      };

      setTraits(traits);
    };

    const getOrCreateVendor = async (data: AddInstantBillFormWidgetFields) => {
      if ('vendorId' in data && data.vendorId) {
        return data.vendorId;
      }

      if (!('vendorName' in data) || ('vendorName' in data && !data.vendorName)) {
        throw new Error('Something went wrong due to a technical issue');
      }
      const { id: createVendorId } = await createVendor({ name: data.vendorName });
      return createVendorId;
    };

    const handleDone = async (data: AddInstantBillFormWidgetFields) => {
      try {
        let uploadedInvoice;

        const vendorId = await getOrCreateVendor(data);
        if (data.invoice) {
          uploadedInvoice = await createFile(data.invoice);
        }

        const billCreationPayload = {
          amount: parseFloat(data.billAmount),
          dueDate: (data.dueDate || today).toISOString(),
          vendorId,
          invoice: {
            number: data.billNumber,
            fileId: uploadedInvoice?.id,
          },
          note: data.note,
          categoryId: data.categoryId,
        };
        const createdBill = await createBill(billCreationPayload);
        trackInstantBillCreation(createdBill);
        setBillTraits();
        onDone(createdBill.id);
      } catch (error) {
        handleFail(error as PlatformError);
      }
    };

    return (
      <AddInstantBillScreen
        defaultValues={defaultValues}
        isSaving={isSaving}
        onClose={onClose}
        onDone={handleDone}
        isLoading={isFetching}
        onGoToDashboard={onGoToDashboard}
        activeAccountingPlatform={activeAccountingPlatform}
        inputVendorAsFreeText={shouldDisplayVendorInputAsFreeText}
      />
    );
  }
);

AddInstantBillActivity.displayName = 'AddInstantBillActivity';
